import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Eye,
  FileText,
  DollarSign,
  Copy,
  Edit,
  Trash2,
  ShoppingCart,
  History,
} from 'lucide-react';
import { RowActionsMenu, RowAction, AuditHistoryModal } from '../../../../../components/StandardPage';
import { SteelOrder } from '../types';
import { deleteSteelOrder, closeSaleOrder, duplicateSteelOrder, updateSteelOrder } from '../api';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import CloseSaleModal from '../CloseSaleModal/index';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import ModalContext from '../../../../../contexts/ModalContext';
import ApiService from '../../../../../services/ApiService';

interface ActionsDropdownProps {
  order: SteelOrder;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ order }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseSaleModal, setShowCloseSaleModal] = useState(false);
  const [showRevertSaleModal, setShowRevertSaleModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setFeedback } = useContext(FeedbackContext);
  const { setModalActive, setModalSection, setSelectedSteelOrder } = useContext(ModalContext);

  const deleteMutation = useMutation({
    mutationFn: (orderId: number) => deleteSteelOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      setFeedback({ message: `Pedido ${order.id} excluído com sucesso!`, isOpen: true, statusOk: true });
      setShowDeleteModal(false);
    },
    onError: (error) => {
      console.error('Erro ao excluir pedido:', error);
      setFeedback({ message: 'Erro ao excluir pedido. Tente novamente.', isOpen: true, statusOk: false });
    },
  });

  const updateSaleStatusMutation = useMutation({
    mutationFn: ({ orderId, status, saleStatus }: { orderId: number; status: boolean | null; saleStatus?: string }) => {
      console.log(`[FRONTEND] Solicitando atualização de status (fechar/cancelar) do pedido ${orderId}...`, status);
      return updateSteelOrder(orderId, { status, ...(saleStatus ? { saleStatus } : {}) });
    },
    onSuccess: (data) => {
      console.log(`[FRONTEND] Status atualizado com sucesso. Retorno da API:`, data);
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['tickets'], refetchType: 'all' });
      window.dispatchEvent(new CustomEvent('pipeline-request-sync', { detail: { externalId: order.id } }));
      setShowCloseSaleModal(false);
      setShowRevertSaleModal(false);
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar status da venda:', error);
      const rawMsg = error?.response?.data?.message;
      const backendMsg = Array.isArray(rawMsg) ? rawMsg.join(', ') : rawMsg;
      setFeedback({
        message: backendMsg || 'Erro ao atualizar o status da venda. Tente novamente.',
        isOpen: true,
        statusOk: false,
      });
    },
  });

  const closeSaleMutation = useMutation({
    mutationFn: (orderId: number) => {
      return closeSaleOrder(orderId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['tickets'], refetchType: 'all' });
      window.dispatchEvent(new CustomEvent('pipeline-request-sync', { detail: { externalId: order.id } }));
      setShowCloseSaleModal(false);
      setFeedback({
        message: 'Venda fechada com sucesso.',
        isOpen: true,
        statusOk: true,
      });
    },
    onError: (error: any) => {
      console.error('Erro ao fechar venda no front:', error);
      const rawMsg = error?.response?.data?.message;
      const backendMsg = Array.isArray(rawMsg) ? rawMsg.join(', ') : rawMsg;
      setFeedback({
        message: backendMsg || 'Erro ao fechar a venda. Verifique os dados do pedido.',
        isOpen: true,
        statusOk: false,
      });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (orderId: number) => duplicateSteelOrder(orderId),
    onSuccess: (duplicatedOrder) => { 
      localStorage.removeItem('reorderedData');
      localStorage.setItem('lastDuplicatedFrom', order.id.toString());
      
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
    },
    onError: (error) => {
      console.error('Erro ao duplicar pedido:', error);
    },
  });

  const handleAction = (action: string) => {
    switch (action) {
      case 'view':
        navigate(`/panel/steel/${order.id}`);
        break;
      case 'edit':
        navigate(`/panel/steel/edit/${order.id}`);
        break;
      case 'close-sale':
        setShowCloseSaleModal(true);
        break;
      case 'revert-sale':
        setShowRevertSaleModal(true);
        break;
      case 'generate-pdf': {
        const generatePdfProgrammatically = async () => {
          try {
            setFeedback({ message: 'Gerando PDF, aguarde...', isOpen: true, statusOk: true });
            const { data: fullOrder } = await ApiService.steelOrders.getOrder(order.id);
            const { pdf } = await import('@react-pdf/renderer');
            const { default: OrderPDFDocument } = await import('../../components/OrderPDFDocument');
            const blob = await pdf(<OrderPDFDocument orderData={fullOrder} />).toBlob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
          } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            setFeedback({ message: 'Erro ao gerar o PDF. Tente novamente.', isOpen: true, statusOk: false });
          }
        };
        generatePdfProgrammatically();
        break;
      }
      case 'invoice':
        setSelectedSteelOrder(order);
        setModalSection('generateOrderInvoice');
        setModalActive(true);
        break;
      case 'duplicate':
        duplicateMutation.mutate(order.id);
        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      case 'audit':
        setShowAuditModal(true);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate(order.id);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmCloseSale = () => {
    updateSaleStatusMutation.mutate({ orderId: order.id, status: true, saleStatus: 'Fechado' });
  };

  const handleCancelSale = () => {
    updateSaleStatusMutation.mutate({ orderId: order.id, status: false, saleStatus: 'Cancelado' });
  };

  const handleCancelCloseSale = () => {
    setShowCloseSaleModal(false);
  };

  const handleConfirmRevertSale = () => {
    updateSaleStatusMutation.mutate({ orderId: order.id, status: null, saleStatus: 'Em orçamento' });
  };

  const actions: RowAction[] = [
    ...(order.status === null || order.status === undefined
      ? [{ key: 'close-sale', label: 'Fechar venda', icon: <ShoppingCart size={16} />, onClick: () => handleAction('close-sale') }]
      : []),
    ...(order.status === true || order.status === false
      ? [{ key: 'revert-sale', label: 'Reverter venda', icon: <ShoppingCart size={16} />, onClick: () => handleAction('revert-sale') }]
      : []),
    { key: 'generate-pdf', label: 'Gerar PDF', icon: <FileText size={16} />, onClick: () => handleAction('generate-pdf') },
    ...(order.status === true
      ? [{ key: 'invoice', label: 'Faturar', icon: <DollarSign size={16} />, onClick: () => handleAction('invoice') }]
      : []),
    { key: 'view', label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleAction('view') },
    { key: 'duplicate', label: 'Duplicar', icon: <Copy size={16} />, onClick: () => handleAction('duplicate') },
    { key: 'edit', label: 'Editar', icon: <Edit size={16} />, onClick: () => handleAction('edit') },
    { key: 'audit', label: 'Auditoria', icon: <History size={16} color="#2563eb" />, onClick: () => handleAction('audit') },
    { key: 'delete', label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleAction('delete') },
  ];

  return (
    <>
      <RowActionsMenu actions={actions} />
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Pedido"
        message={`Tem certeza que deseja excluir o pedido ${order.id}? Esta ação não pode ser desfeita.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: handleCancelDelete,
            variant: 'outline'
          },
          {
            label: deleteMutation.isPending ? 'Excluindo...' : 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
      <ConfirmationModal
        isOpen={showRevertSaleModal}
        title="Reverter Venda"
        message={`Tem certeza que deseja reverter a venda do pedido ${order.id}? Ele volta para "Em Aberto" e sai da produção.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setShowRevertSaleModal(false),
            variant: 'outline'
          },
          {
            label: updateSaleStatusMutation.isPending ? 'Revertendo...' : 'Reverter',
            onClick: handleConfirmRevertSale,
            variant: 'danger'
          }
        ]}
      />
      <CloseSaleModal
        isOpen={showCloseSaleModal}
        onClose={handleCancelCloseSale}
        onConfirm={handleConfirmCloseSale}
        onCancelSale={handleCancelSale}
        orderId={order.id}
        isLoading={updateSaleStatusMutation.isPending}
      />
      {showAuditModal && (
        <AuditHistoryModal
          tableName="Order"
          recordId={order.id}
          recordTitle={`Pedido ${order.id}`}
          onClose={() => setShowAuditModal(false)}
        />
      )}
    </>
  );
};

export default ActionsDropdown;
